import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Row, Col, Table } from "react-bootstrap";
import { useToasts } from "react-toast-notifications";

import Head from "@/components/Content/Head";
import BasePagination from "@/components/Pagination/BasePagination";
import Fetching from "@/components/Fetching/Fetching";
import CodesTableHead from "./CodesTableHead";
import CodesTableBody from "./CodesTableBody";
import BasicConfirm from "@/components/Confirm/BasicConfirm";
import DealersForm from "./CodesForm/CodesForm";
import classes from "../maintainers.module.scss";

import { useCodesPaginate, useMutateCode, useMutateDeleteCode, useMutateRestoreCode } from "@/hooks/maintainers/codes";
import { useRefreshInventory } from "@/hooks/inventories/useInventories";
import { base_list } from "./codes.constants";
import ButtonFilter from "@/components/Filter/ButtonFilter";
import CodesFilter from "./CodesFilter";

const Codes = () => {
	const { addToast } = useToasts();
	const { search } = useSelector((state) => state.auth.filter);
	const { option } = useParams();

	const [base, setBase] = useState({ ...base_list });
	const [showFilter, setShowFilter] = useState(false);
	const [showDelete, setShowDelete] = useState(false);
	const [showRestore, setShowRestore] = useState(false);
	const [selected, setSelected] = useState("");

	useEffect(() => {
		setPage(1);
		// eslint-disable-next-line
	}, [search]);

	const { data, isLoading, isFetching } = useCodesPaginate({
		...base,
		search,
	});
	const { mutate: mutateSave, isLoading: isLoadingSave } = useMutateCode({
		...base,
		search,
	});
	const { mutate: mutateDelete, isLoading: isLoadingDelete } = useMutateDeleteCode({
		...base,
		search,
	});
	const { mutate: mutateRestore, isLoading: isLoadingRestore } = useMutateRestoreCode({
		...base,
		search,
	});

	const { mutate: mutateRefresh, isLoading: isLoadingRefresh } = useRefreshInventory({
		...base,
		search,
	});

	const { codes: list } = data ? data : {};
	const { last_page, total, from, to } = list ? list : {};

	const handleDelete = (item) => {
		setSelected(item.id);

		setShowDelete(true);
	};

	const restoreDelete = (item) => {
		setSelected(item.id);

		setShowRestore(true);
	};

	const confirmDelete = () => {
		mutateDelete(
			{ ...base, search, selected },
			{
				onSuccess: () => {
					setSelected("");
					setShowDelete(false);
					addToast("Código eliminado correctamente.", {
						appearance: "success",
					});
				},
			},
		);
	};

	const confirmRestore = (item_selected = null) => {
		let realSelected = item_selected ? item_selected : selected;

		mutateRestore(
			{ ...base, search, selected: realSelected },
			{
				onSuccess: () => {
					setSelected("");
					setShowRestore(false);
					addToast("Código restaurado correctamente.", {
						appearance: "success",
					});
				},
			},
		);
	};

	const confirmSave = (data) => {
		mutateSave(
			{ ...base, search, params: { ...data } },
			{
				onSuccess: () => {
					addToast("Aptitud guardada correctamente.", {
						appearance: "success",
					});
				},
			},
		);
	};

	const handleRefresh = (item) => {
		const form = {
			detail: item,
			base: { ...base, search },
		};

		mutateRefresh(form, {
			onSuccess: () => {
				addToast("Código actualizado correctamente.", {
					appearance: "success",
				});
			},
		});
	};

	const setPage = (value) => setBase({ ...base, page: value });
	const setOrder = (value) => setBase({ ...base, order: value });
	const setOrderType = (value) => setBase({ ...base, orderType: value });
	const setPaginate = (value) => setBase({ ...base, paginate: value });

	const applyFilter = (params) => {
		let newBase = { ...base };
		if (params.filterCustomer !== base.customer_id) {
			newBase = { ...newBase, customer_id: params.filterCustomer };
		}
		if (params.filterStatus !== base.status) {
			newBase = { ...newBase, status: params.filterStatus };
		}
		setBase(newBase);
		setShowFilter(false);
	};

	if (isLoading || isLoadingDelete || isLoadingRestore || isLoadingRefresh) {
		return <Fetching />;
	}

	return (
		<>
			<Head title="Códigos" button={false} back_url={"/mantenedores"} isFetching={isFetching} />

			<ButtonFilter origin="main" size_md={12} showFilter={showFilter} setShowFilter={setShowFilter} />

			<Row className={` ${classes.main_row}`}>
				{showFilter && <CodesFilter base={base} applyFilter={applyFilter} />}

				<Col md={showFilter ? 9 : 12} className={classes.main_col}>
					<Row className={`${classes.header} `}>
						<ButtonFilter origin="actions" size_md={1} showFilter={showFilter} setShowFilter={setShowFilter} show_back={false} />
					</Row>

					<Row className={classes.table}>
						<div className={classes.table_div}>
							<Table className={classes.table_desktop}>
								<CodesTableHead order={base.order} setOrder={setOrder} orderType={base.orderType} setOrderType={setOrderType} />

								<CodesTableBody base={base} handleDelete={handleDelete} handleRefresh={handleRefresh} restoreDelete={restoreDelete} />
							</Table>
						</div>
					</Row>

					<BasePagination
						page={base.page}
						paginate={base.paginate}
						last_page={last_page}
						quantity={total}
						from={from}
						to={to}
						setPage={setPage}
						setPaginate={setPaginate}
					/>
				</Col>
			</Row>

			<BasicConfirm
				showConfirm={showRestore}
				setShowConfirm={setShowRestore}
				handleAction={confirmRestore}
				showValidation={true}
				subMessage={"Si el código esta en uso actualmente, se le asignará un nuevo código."}
				textValidation={"restaurar código"}
			/>

			<BasicConfirm
				showConfirm={showDelete}
				setShowConfirm={setShowDelete}
				handleAction={confirmDelete}
				showValidation={true}
				textValidation={"eliminar código"}
			/>

			{option && <DealersForm confirmSave={confirmSave} isLoadingSave={isLoadingSave} />}
		</>
	);
};

export default Codes;
